import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Backdrop,
  TextField,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, addDays, differenceInMinutes } from 'date-fns';
import {
  fetchAvailability,
  getCacheAge,
  clearCache,
  findNearbyAvailableDates,
  isSessionAvailable,
  RezdySession
} from '../services/rezdyService';
import {
  shouldDisplayProduct,
  isTongariroCrossing
} from '../config/tour-mappings';
import {
  FilterType,
  FILTER_ORDER,
  tourMatchesFilter,
  isTourHidden,
  getTourSortOrder
} from '../config/filter-mappings';
import TourCard from './TourCard';

const AvailabilityChecker: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [sessions, setSessions] = useState<RezdySession[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<RezdySession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('All');
  const [suggestedDates, setSuggestedDates] = useState<Date[]>([]);

  // Calculate max date (30 days from today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = addDays(today, 30);

  // Load availability when date changes
  useEffect(() => {
    if (selectedDate) {
      loadAvailability(selectedDate);
    }
  }, [selectedDate]);

  // Apply filter when sessions or filter changes
  useEffect(() => {
    applyFilter();
  }, [sessions, selectedFilter]);

  const loadAvailability = async (date: Date) => {
    setLoading(true);
    setError(null);
    setSuggestedDates([]);

    try {
      const availableSessions = await fetchAvailability(date);
      
      // Filter out non-tour products
      const tourSessions = availableSessions.filter(session =>
        shouldDisplayProduct(session.productName || '', session.productCode)
      );

      setSessions(tourSessions);
      setLastUpdated(new Date());

      // Check if all tours are fully booked
      const hasAvailability = tourSessions.some(s => isSessionAvailable(s));
      if (!hasAvailability && tourSessions.length > 0) {
        // Find nearby dates with availability
        const nearby = await findNearbyAvailableDates(date);
        setSuggestedDates(nearby);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
      setError('Unable to load availability. Please try again.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    // First, filter out hidden tours (except when showing all)
    let availableSessions = sessions.filter(session => {
      const productName = session.productName || '';
      return !isTourHidden(productName);
    });
    
    // Then apply the selected filter
    if (selectedFilter === 'All') {
      setFilteredSessions(availableSessions);
    } else {
      const filtered = availableSessions.filter(session => {
        const productName = session.productName || '';
        return tourMatchesFilter(productName, selectedFilter);
      });
      setFilteredSessions(filtered);
    }
  };

  const handleRefresh = () => {
    clearCache();
    if (selectedDate) {
      loadAvailability(selectedDate);
    }
  };

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      setSelectedDate(newDate);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setSelectedFilter(filter);
  };

  const handleDateNavigation = (days: number) => {
    if (!selectedDate) return;
    
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    
    // Check bounds
    if (newDate >= today && newDate <= maxDate) {
      setSelectedDate(newDate);
    }
  };

  const getCacheAgeText = (): string => {
    if (!selectedDate || !lastUpdated) return '';
    
    const cacheAge = getCacheAge(selectedDate);
    if (cacheAge === null) return 'Just now';

    const minutes = Math.floor(cacheAge / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Website link message - always visible */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Please check our website for up-to-date availability{' '}
          <Link 
            href="https://adriftnz.co.nz/guided-tongariro-walks/" 
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            here
          </Link>
          .
        </Alert>

        {/* Date Picker */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="flex-start" gap={1} flex={1} minWidth="250px">
              <Button
                variant="outlined"
                onClick={() => handleDateNavigation(-1)}
                disabled={loading || !selectedDate || selectedDate <= today}
                sx={{ minWidth: '40px', px: 1 }}
                aria-label="Previous day"
              >
                <ChevronLeftIcon />
              </Button>
              <Box flex={1}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={today}
                  maxDate={maxDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Showing availability for the next 30 days only'
                    }
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                onClick={() => handleDateNavigation(1)}
                disabled={loading || !selectedDate || selectedDate >= maxDate}
                sx={{ minWidth: '40px', px: 1 }}
                aria-label="Next day"
              >
                <ChevronRightIcon />
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Tour Type Filters */}
          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Filter by tour type:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {FILTER_ORDER.map(filter => (
                <Chip
                  key={filter}
                  label={filter}
                  onClick={() => handleFilterChange(filter)}
                  color={selectedFilter === filter ? 'primary' : 'default'}
                  variant={selectedFilter === filter ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Loading Overlay */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <Box textAlign="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" mt={2}>
              Loading availability...
            </Typography>
          </Box>
        </Backdrop>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {!loading && !error && selectedDate && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                Availability for {format(selectedDate, 'dd MMMM yyyy')}
              </Typography>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {getCacheAgeText()}
                </Typography>
              )}
            </Box>

            {/* No results message */}
            {filteredSessions.length === 0 && sessions.length > 0 && selectedFilter !== 'All' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No {selectedFilter} tours on this date.{' '}
                <Link
                  component="button"
                  onClick={() => handleFilterChange('All')}
                  sx={{ fontWeight: 600 }}
                >
                  Show all tours
                </Link>
              </Alert>
            )}

            {/* All tours fully booked */}
            {sessions.length > 0 && !sessions.some(s => isSessionAvailable(s)) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                All tours are fully booked on this date.
                {suggestedDates.length > 0 && (
                  <Box mt={1}>
                    Try these dates instead:{' '}
                    {suggestedDates.map((date, idx) => (
                      <React.Fragment key={date.toISOString()}>
                        {idx > 0 && ' or '}
                        <Link
                          component="button"
                          onClick={() => setSelectedDate(date)}
                          sx={{ fontWeight: 600 }}
                        >
                          {format(date, 'dd MMM')}
                        </Link>
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {/* No tours available at all */}
            {sessions.length === 0 && (
              <Alert severity="info">
                No tours scheduled for this date.
              </Alert>
            )}

            {/* Tour Cards */}
            {[...filteredSessions]
              .sort((a, b) => {
                const aName = a.productName || '';
                const bName = b.productName || '';
                
                // Get CSV sort order for both tours
                const aOrder = getTourSortOrder(aName);
                const bOrder = getTourSortOrder(bName);
                
                // If both are in CSV order, sort by CSV order
                if (aOrder !== Infinity && bOrder !== Infinity) {
                  // If same tour, sort by start time
                  if (aOrder === bOrder) {
                    return a.startTimeLocal.localeCompare(b.startTimeLocal);
                  }
                  return aOrder - bOrder;
                }
                
                // If only one is in CSV order, prioritize it
                if (aOrder !== Infinity && bOrder === Infinity) return -1;
                if (aOrder === Infinity && bOrder !== Infinity) return 1;
                
                // If neither is in CSV order, fall back to Tongariro Crossing priority, then start time
                const aIsTongariro = isTongariroCrossing(a.productCode);
                const bIsTongariro = isTongariroCrossing(b.productCode);
                
                if (aIsTongariro && !bIsTongariro) return -1;
                if (!aIsTongariro && bIsTongariro) return 1;
                
                // Otherwise sort by start time
                return a.startTimeLocal.localeCompare(b.startTimeLocal);
              })
              .map((session, index) => (
                <TourCard
                  key={`${session.productCode}-${session.startTime}-${index}`}
                  session={session}
                />
              ))}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AvailabilityChecker;


